import React from 'react';
import { useTranslation } from 'react-i18next';

import { useGetThemePerformance } from '~/hooks/useGetThemePerformance';
import { ThemeMarketReactionChart } from '~/modules/market-reaction-charts/theme-market-reaction-chart/ThemeMarketReactionChart';
import { formatNumber } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/utils/number-formatter';

import * as S from './CatalystThemeMarketReaction.styles';

const MOCK_INTRADAY_VALUE = 0.01;

export interface CatalystThemeMarketReactionProps {
  themeId?: string;
}

export const CatalystThemeMarketReaction = ({
  themeId,
}: CatalystThemeMarketReactionProps) => {
  const { t } = useTranslation(['insights']);
  const { data } = useGetThemePerformance({ themeId });

  if (!data) {
    return null;
  }

  const horizonTranslationMap = {
    intraday: t('insights:catalyst.horizons.intraday'),
    '1d': t('insights:catalyst.horizons.1d'),
    '1w': t('insights:catalyst.horizons.1w'),
    '1m': t('insights:catalyst.horizons.1m'),
    '3m': t('insights:catalyst.horizons.3m'),
  };
  const performanceData = Object.entries(data.performance).reduce(
    (acc, [key, val]) => {
      const horizon =
        horizonTranslationMap[key as keyof typeof horizonTranslationMap];

      if (horizon) {
        acc[horizon] = val;
      }

      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <>
      {data.data && (
        <ThemeMarketReactionChart
          createdAt="2025-10-31T00:00:00Z"
          tsData={data.data}
        />
      )}
      <S.HorizonSelector>
        <S.HorizonItem>
          <S.HorizonItemTitle>
            {horizonTranslationMap.intraday}
          </S.HorizonItemTitle>
          <S.HorizonItemValue
            $isNegative={!!MOCK_INTRADAY_VALUE && MOCK_INTRADAY_VALUE < 0}
          >
            {formatNumber({ isPercentage: true, value: MOCK_INTRADAY_VALUE })}
          </S.HorizonItemValue>
        </S.HorizonItem>
        {Object.entries(performanceData).map(([key, value]) => (
          <S.HorizonItem key={key}>
            <S.HorizonItemTitle>{key}</S.HorizonItemTitle>
            <S.HorizonItemValue $isNegative={!!value && value < 0}>
              {formatNumber({ isPercentage: true, value: value })}
            </S.HorizonItemValue>
          </S.HorizonItem>
        ))}
      </S.HorizonSelector>
    </>
  );
};
